import React, { useState, useEffect } from "react";
import { innovatorAPI } from "../../services/api";
import { User, DollarSign, TrendingUp, ChevronRight, Award, Briefcase } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import skyLoader from "../../assets/home-assets/skyLoader.gif";

function InnovatorsDashboard() {
  const [innovators, setInnovators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchInnovators = async () => {
    try {
      setLoading(true);
      const response = await innovatorAPI.getAll();
      setInnovators(response.data);
    } catch (error) {
      setError(error.message || "Error loading innovators");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInnovators();
  }, []);

  const calculateTotalInvestment = (projectsInvested) => {
    return projectsInvested?.reduce((sum, p) => sum + (p.investmentAmount || 0), 0) || 0;
  };

  const calculateTotalStaked = (projectsStaked) => {
    return projectsStaked?.reduce((sum, p) => sum + (p.stakedAmount || 0), 0) || 0;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <img 
          src={skyLoader} 
          alt="Loading..." 
          className="w-100 h-100 object-contain"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-500 px-4 py-3 rounded-lg">
        <p>{error}</p>
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div className="px-4 mt-16">
      {/* Header with Title and View All Link */}
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-800 leading-tight">
          Top Innovators
        </h2>
        <Link
          to="/innovators"
          className="flex items-center text-indigo-600 hover:text-indigo-800 transition-colors text-sm font-medium"
        >
          View All
          <ChevronRight size={16} className="ml-1" />
        </Link>
      </div>

      {innovators.length === 0 ? (
        <div className="bg-white rounded-xl p-6 text-center border border-gray-200 shadow-md">
          <h3 className="text-lg font-medium text-gray-700">No innovators found</h3>
          <p className="text-gray-500 mt-2">
            Be the first to join our platform as an innovator!
          </p>
        </div>
      ) : (
        <motion.div 
          className="space-y-4"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {innovators.slice(0, 5).map((innovator, index) => {
            const totalInvestment = calculateTotalInvestment(innovator.projectsInvested);
            const totalStaked = calculateTotalStaked(innovator.projectsStaked);

            return (
              <motion.div
                key={innovator.walletAddress}
                className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-indigo-200 shadow-md transition-all duration-300 flex flex-col md:flex-row"
                variants={itemVariants}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                {/* Innovator Info */}
                <div className="flex items-center p-4 md:w-1/2 border-b md:border-b-0 md:border-r border-gray-100">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md mr-4">
                    <User size={24} className="text-white" />
                  </div>
                  <div>
                    <Link 
                      to={`/innovators/${innovator.walletAddress}`}
                      className="text-xl font-bold text-gray-800 hover:text-indigo-600 transition-colors"
                    >
                      {innovator.name || "Anonymous Innovator"}
                    </Link>
                    <div className="flex items-center mt-1 text-sm text-gray-600">
                      <span>{innovator.walletAddress|| "-"}</span>
                    </div>
                    
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between p-4 md:w-1/2 bg-gray-50">
                  <div className="text-center px-4">
                    <div className="flex items-center justify-center text-gray-500 text-xs mb-1">
                      <DollarSign size={14} className="mr-1" />
                      <span>Investment</span>
                    </div>
                    <div className="flex items-center justify-center">
                      <span className="text-indigo-600 font-bold">{totalInvestment}</span>
                      <span className="text-indigo-500 ml-1 text-sm">CEL</span>
                    </div>
                  </div>

                  <div className="text-center px-4">
                    <div className="flex items-center justify-center text-gray-500 text-xs mb-1">
                      <TrendingUp size={14} className="mr-1" />
                      <span>Staked</span>
                    </div>
                    <div className="flex items-center justify-center">
                      <span className="text-blue-600 font-bold">{totalStaked}</span>
                      <span className="text-blue-500 ml-1 text-sm">CEL</span>
                    </div>
                  </div>

                  <Link
                    to={`/innovators/${innovator.walletAddress}`}
                    className="rounded-full p-2 ml-2 hover:bg-indigo-100 transition-colors"
                  >
                    <ChevronRight size={16} className="text-indigo-600" />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      )}
    </div>
  );
}

export default InnovatorsDashboard;
